import { CarPageContextProvider } from '@/components/car/CarPageContext';
import type { Page, PageSectionWithLocale } from '@/hooks/useLpg';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import { petTemplateConfig } from '@/pageConfig/pet/pet';
import { StyledSection } from '@/pageTemplate/HomeownersPageTemplate';
import { LifePageWrapper } from '@/pageTemplate/LifePageTemplate';
import { Flex } from '@lemonade-hq/cdk';
import type { Locale } from '@lemonade-hq/lemonation';
import dynamic from 'next/dynamic';
import { Fragment } from 'react';
import type React from 'react';
import { useMemo } from 'react';
import { getExtraPropsBySectionType } from './getExtraPropsBySectionType';
import { COMPONENTS_MAP } from './lpgTypesAndDefinitions';
import type { Components, ProductsByHeroSection } from './lpgTypesAndDefinitions';
import type { ComponentProps, UnionToIntersection } from './tsHelpers';

const PetPublications = dynamic(() => import('@/components/pet/Publications').then(mod => mod.default));

const PuppyKitten = dynamic(() => import('@/components/pet/PuppyKitten/PuppyKitten').then(mod => mod.default));

const absoluteScrollSectionsType = ['car_forest', 'car_bottom_cta', 'car_claims'];

const Section: React.FC<
  PageSectionWithLocale & {
    product: ProductsByHeroSection | undefined;
    isCarPpm: boolean;
  }
> = ({ type, props, locale, product, isCarPpm }) => {
  const { smoothRelativeScroll, absoluteScroll } = useSmoothScroll();

  const scrollType = useMemo(() => {
    return absoluteScrollSectionsType.includes(type) ? absoluteScroll : smoothRelativeScroll;
  }, [type, absoluteScroll, smoothRelativeScroll]);

  const finalProps = useMemo(
    () => getExtraPropsBySectionType({ type, props, locale, product, isCarPpm }),
    [type, props, locale, product, isCarPpm],
  ) as UnionToIntersection<ComponentProps<Components>>;

  if (type == null) return null;

  if (COMPONENTS_MAP[type] == null) {
    console.error(`type: ${type} is missing in sections map`);
  }

  const Component = COMPONENTS_MAP[type];
  if (Component == null) return null;

  // this is an small patch exception for now since LPG does not returns Publications section data and we do render it after the hero in pet template, we can remove it when LPG sends this section
  if (product === 'pet' && type === 'pet_hero') {
    return (
      <>
        <Component {...finalProps} t={scrollType} />;
        <PetPublications {...petTemplateConfig[locale].publications} />
      </>
    );
  }

  // this is an small patch exception for now since LPG does not returns PuppyKitten section data and we do render it before the FAQ in pet template, we can remove it when LPG sends this section
  if (product === 'pet' && type === 'pet_faq') {
    return (
      <>
        <PuppyKitten />
        <Component {...finalProps} t={scrollType} />;
      </>
    );
  }

  // we wrap the giving back section in a dark background and apply some styles in homeowners template
  if (type === 'ho_giving_back') {
    return (
      <StyledSection darkBackground>
        <Component {...finalProps} t={scrollType} />
      </StyledSection>
    );
  }

  if (product === 'car') {
    return (
      <CarPageContextProvider isEmbeddedPage={false}>
        <Flex textAlign="center" flexDirection="column" alignItems="center" overflow="hidden">
          <Component {...finalProps} t={scrollType} />
        </Flex>
      </CarPageContextProvider>
    );
  }

  return <Component {...finalProps} t={scrollType} />;
};

interface LpgSectionProps {
  page: Page | undefined;
  locale: Locale;
  product: ProductsByHeroSection | undefined;
  isCarPpm: boolean;
}

const CustomPageWrapperByProduct = {
  life: LifePageWrapper,
  default: Fragment,
  pet: Fragment,
  renters: Fragment,
  home: Fragment,
  car: Fragment,
} as const;

export const LpgSection: React.FC<LpgSectionProps> = ({ page, locale, product, isCarPpm }) => {
  if (page == null) return null;
  const PageWrapper = CustomPageWrapperByProduct[product ?? 'default'];

  return (
    <PageWrapper>
      {page.sections.map(({ type, props }, index) => {
        return (
          <Section
            key={`${type}-${
              // biome-ignore lint/suspicious/noArrayIndexKey: it's a unique key
              index
            }`}
            props={props}
            type={type}
            locale={locale}
            product={product}
            isCarPpm={isCarPpm}
          />
        );
      })}
    </PageWrapper>
  );
};
